/* eslint-disable react/display-name */
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { FileRejection, useDropzone } from "react-dropzone";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BiHeadphone,
  BiImage,
  BiMusic,
  BiPlay,
  BiTrash,
  BiUpload,
  BiVideo
} from "react-icons/bi";
import * as Yup from "yup";

import { useAppSelector } from "../../../../app/hook";
import { useUploadToCDNMutation } from "../../../../app/services/chatbot-rtk.services";
import { AlertsType } from "../../../../constants/alerts.constant";
import {
  selectUseId,
  selectUserProfile
} from "../../../../features/auth/auth.slice";
import { BlockType } from "../../../../interfaces/models/Knowledge/scenario.interface";
import AddNoteButton from "../AddNoteButton";
import DeleteButton from "../DeleteButton";
import NodeParentChanger from "../NodeParentChanger";
import NoteForm, { getNoteSchema, INoteForm } from "../NoteForm";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";
import ScenarioSide from "./video-scenario-side";

export interface IFormData extends INoteForm {
  label: string;
  title: string;
  media?: {
    category: "image" | "audio" | "video";
    path: string;
  };
}

export const MacBotMediaForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<void>;
  deleteScenarioBlock: (block: any) => Promise<void>;
  changeScenarioBlock: (block: any) => Promise<void>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();

  const { user_identification } = useAppSelector(selectUserProfile);
  const userId = useAppSelector(selectUseId);
  const [isDelete, setIsDelete] = useState(true);
  const [files, setFiles] = useState<any[]>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);
  const [currentUploadedFiles, setCurrentUploadedFiles] = useState<any[]>([]);

  const [cdnUpload, cdnUploadResults] = useUploadToCDNMutation();

  const schema = Yup.object({
    title: Yup.string()
      .required(t(AlertsType.ISKBKO025))
      .max(30, t("Wizard/validationLabels/key16")),
    label: Yup.string().required(
      t("MacBotMediaForm/key2") +
        " " +
        t("MacBotMediaForm/scenario_variable_name_required")
    ),
    media: Yup.object().shape({
      category: Yup.string()
        .oneOf(
          ["image", "audio", "video"],
          t("MacBotMediaForm/scenario_category_required")
        )
        .required(t("MacBotMediaForm/scenario_category_required")),
      path: Yup.string().optional().nullable()
    }),
    ...getNoteSchema(t)
  });

  const {
    register,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
    handleSubmit,
    getValues,
    setError,
    clearErrors
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any
  });
  const { fields } = useFieldArray({
    control,
    name: "notes"
  });

  const formatExtensions: Record<string, string[]> = {
    image: [".png", ".jpeg", ".jpg", ".gif"],
    audio: [".mp3", ".aac", ".ogg", ".wav", ".webm", ".3gp", ".mp4", ".m4a"],
    video: [
      ".mp4",
      ".mpeg",
      ".webm",
      ".ogg",
      ".mov",
      ".m4v",
      ".mkv",
      ".avi",
      ".3gp"
    ]
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 30 * 1024 * 1024, //30mb max size
    accept:
      getValues("media.category") === "image"
        ? { "image/*": [".png", ".jpeg", ".jpg", ".gif"] }
        : getValues("media.category") === "audio"
        ? {
            "audio/mpeg": [".mp3"],
            "audio/aac": [".aac"],
            "audio/ogg": [".ogg"],
            "audio/wav": [".wav"],
            "audio/webm": [".webm"],
            "audio/3gp": [".3gp"],
            "audio/mp4": [".mp4", ".m4a"]
          }
        : {
            "video/mp4": [".mp4"],
            "video/mpeg": [".mpeg"],
            "video/webm": [".webm"],
            "video/ogg": [".ogg"],
            "video/mov": [".mov"],
            "video/m4v": [".m4v"],
            "video/mkv": [".mkv"],
            "video/avi": [".avi"],
            "video/3gp": [".3gp"]
          },
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          category: watch("media.category")
        })
      );

      setFiles(newFiles);

      setCurrentUploadedFiles(newFiles);

      setFileRejections([]);

      clearErrors("media.path");
    },
    onDropRejected(fileRejections, event) {
      setFileRejections(fileRejections);
    }
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.name}>
      {file.name} -{" "}
      {Intl.NumberFormat("en", {
        notation: "compact",
        style: "unit",
        unit: "byte",
        unitDisplay: "narrow"
      }).format(file.size)}
      <ul>
        {errors.map((e) => (
          <li
            key={e.code}
            className="tw-text-red-600 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-3"
          >
            {e.code === "file-too-large"
              ? t("MacBotMediaForm/key8")
              : e.message}
          </li>
        ))}
      </ul>
    </li>
  ));

  const mediaTypes: Array<{
    icon: React.ReactNode;
    label: string;
    value: "image" | "audio" | "video";
  }> = [
    {
      icon: <BiImage size={22} className="tw-text-primary" />,
      label: t("MacBotMediaForm/key9"),
      value: "image"
    },
    {
      icon: <BiHeadphone size={22} className="tw-text-primary" />,
      label: t("MacBotMediaForm/key10"),
      value: "audio"
    },
    {
      icon: <BiVideo size={22} className="tw-text-primary" />,
      label: t("MacBotMediaForm/key11"),
      value: "video"
    }
  ];

  const handleDeleteSelectedFile = () => {
    setFiles([]);
    setFileRejections([]);
    setCurrentUploadedFiles([]);

    setValue("media.path", "");

    clearErrors(["media.category", "media.path"]);
  };

  const handleManageCategoryChange = (
    selectedCategory: "image" | "audio" | "video"
  ) => {
    if (
      currentUploadedFiles &&
      currentUploadedFiles.length > 0 &&
      currentUploadedFiles[0].category &&
      currentUploadedFiles[0].category === selectedCategory
    )
      setFiles([...currentUploadedFiles]);
    else setFiles([]);

    setFileRejections([]);

    clearErrors(["media.category", "media.path"]);
  };

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const getFileCategory = (file: any) => {
    const extension = file.name.split(".").pop().toLowerCase();

    if (formatExtensions.image.includes(`.${extension}`))
      return "image" as const;
    else if (formatExtensions.audio.includes(`.${extension}`))
      return "audio" as const;
    else if (formatExtensions.video.includes(`.${extension}`))
      return "video" as const;
    else return null;
  };

  const onSubmit = (data: any) => {
    if (!files || files.length <= 0)
      return setError("media.path", {
        type: "required",
        message: t("MacBotMediaForm/key12")
      });

    const formData = new FormData();
    formData.append(
      "files",
      files[0],
      user_identification + "_" + files[0].name
    );

    cdnUpload(formData)
      .unwrap()
      .then((result) => {
        if (result[0]?.is_successful) {
          const path = result[0].cdnPath;
          const name = result[0].original_name;
          const size = result[0].size_byte;

          if (itemData) {
            const _data = {
              ...itemData,
              title: data.title,
              label: data.label,
              id: itemData.id,
              type: BlockType.Media,
              parent: itemData.parent,
              ...data,
              media: {
                ...data.media,
                path,
                name,
                size
              }
            };
            changeScenarioBlock(_data).then();
          } else {
            const _data = {
              type: BlockType.Media,
              title: data.title,
              label: data.label,
              parent: parentId,
              ...data,
              media: {
                ...data.media,
                path,
                name,
                size
              }
            };
            createScenarioBlock(_data).then();
          }

          reset();
          setShow(false);
        } else
          setError("media.path", {
            type: "custom",
            message: t("MacBotMediaForm/key13")
          });
      })
      .catch((err) => {
        setError("media.path", {
          type: "custom",
          message: err.data.message || err.message || t("MacBotMediaForm/key13")
        });
      });
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? "",
        label: itemData?.label ?? "",
        media: {
          category: itemData?.media.category ?? "",
          path: itemData?.media.path ?? ""
        },
        notes: itemData?.notes
      });
    }
  }, [itemData]);

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  useEffect(() => {
    const createFileFromEditData = async (media: {
      path: string;
      name: string;
      size: number;
    }) => {
      try {
        const response = await fetch(media.path);
        const blob = await response.blob();

        const file = new File([blob], media.name, {
          type: response.headers.get("content-type") as string
        });

        Object.assign(file, {
          preview: media.path,
          category: getFileCategory(file)
        });

        setCurrentUploadedFiles([file]);

        return setFiles([file]);
      } catch (error) {
        console.error("Error creating file:", error);
        return null;
      }
    };

    if (itemData?.media && Object.keys(itemData.media).length > 0)
      createFileFromEditData(itemData.media);
  }, [itemData?.media]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowMediaForm}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col>
              <div className="tw-self-stretch tw-h-[68px] tw-flex-col tw-justify-center tw-items-start tw-gap-2 tw-flex">
                <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t("MacBotMediaForm/key14")}
                </div>
                <Form.Group className="tw-w-full">
                  <Form.Control
                    isInvalid={!!errors.title}
                    max={31}
                    maxLength={31}
                    {...register("title")}
                    className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  />
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-block font-poppins"
                  >
                    {errors.title?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <div />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label
                  htmlFor=""
                  className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
                >
                  {t("MacBotMediaForm/key2")}
                </Form.Label>
                <Form.Control
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  isInvalid={!!errors.label}
                  {...register(`label`)}
                />
                <Form.Control.Feedback
                  type="invalid"
                  className="d-block font-poppins"
                >
                  {errors.label?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <div>
            <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t("MacBotMediaForm/key4")}
            </div>

            <Row className="tw-mt-4 tw-gap-y-3">
              {mediaTypes.map((category, index) => (
                <Col key={category.value} xs={12} md={4}>
                  <Controller
                    control={control}
                    name="media.category"
                    render={({ field }) => (
                      <button
                        onClick={() => {
                          setValue("media.category", category.value);

                          clearErrors(["media.category", "media.path"]);

                          handleManageCategoryChange(category.value);
                        }}
                        type="button"
                        className={`tw-w-full tw-p-4 tw-bg-white tw-rounded-md tw-border tw-border-solid ${
                          field.value === category.value
                            ? "tw-border-blue-500"
                            : "tw-border-gray-200"
                        } tw-flex-col tw-justify-center tw-items-center tw-gap-[7px] tw-flex`}
                      >
                        <div className="tw-p-2 tw-bg-gray-100 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-2.5 tw-flex">
                          {category.icon}
                        </div>
                        <div className="tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {category.label}
                        </div>
                      </button>
                    )}
                  />
                </Col>
              ))}

              {!!errors.media?.category && (
                <div className="tw-text-red-600 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-3">
                  {errors.media?.category?.message}
                </div>
              )}
            </Row>
          </div>

          {watch("media.category") && (
            <section className="file-manager tw-w-full">
              {files.length <= 0 && (
                <div {...getRootProps({ className: "dropzone" })}>
                  <input {...getInputProps()} />
                  <div className="tw-min-h-[156px] tw-w-full tw-px-4 tw-py-9 tw-bg-gray-100 tw-rounded-md tw-border-dashed tw-border-2 tw-border-gray-300 tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-inline-flex">
                    <div className="tw-p-2 tw-bg-gray-200 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-2.5 tw-inline-flex">
                      <BiUpload className="tw-text-primary" size={22} />
                    </div>
                    <div className="tw-flex-col tw-justify-center tw-items-center tw-flex">
                      <div className="tw-w-[188px] tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("MacBotMediaForm/key5")}
                      </div>
                      <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-text-center">
                        {t("MacBotMediaForm/key6")}{" "}
                        {formatExtensions[getValues("media.category")].map(
                          (ext, index) => (
                            <span key={ext}>
                              {ext.replace(".", "").toUpperCase()}
                              {index !==
                                formatExtensions[getValues("media.category")]
                                  .length -
                                  1 && ", "}
                            </span>
                          )
                        )}
                        .
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!!errors.media?.path && (
                <div className="tw-text-red-600 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-3">
                  {errors.media?.path?.message}
                </div>
              )}

              <ul className="tw-mt-4">{fileRejectionItems}</ul>

              <aside className="tw-mt-3">
                {files.map((file) => (
                  <div
                    key={file.name}
                    className="tw-w-full tw-h-[88px] tw-p-6 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex"
                  >
                    <div className="tw-self-stretch tw-justify-between tw-items-center tw-flex">
                      <div className="tw-justify-start tw-items-center tw-gap-3 tw-flex tw-shrink-0">
                        {watch("media.category") === "image" ? (
                          <img
                            alt={file.name}
                            className="tw-w-10 tw-h-10 tw-rounded tw-object-cover"
                            src={file.preview}
                            // onLoad={() => {
                            //   URL.revokeObjectURL(file.preview);
                            // }}
                          />
                        ) : watch("media.category") === "audio" ? (
                          <div className="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded tw-flex tw-flex-col tw-items-center tw-justify-center">
                            <BiMusic
                              size={22}
                              className="tw-text-gray-400 tw-text-xl"
                            />
                          </div>
                        ) : (
                          <div className="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded tw-flex tw-flex-col tw-items-center tw-justify-center">
                            <BiVideo
                              size={22}
                              className="tw-text-gray-400 tw-text-xl"
                            />
                          </div>
                        )}
                        <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex">
                          <div className="tw-text-left tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-max-w-40 tw-text-wrap tw-break-words">
                            {file.name}
                          </div>
                          <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                            {Intl.NumberFormat("en", {
                              notation: "compact",
                              style: "unit",
                              unit: "byte",
                              unitDisplay: "narrow"
                            }).format(file.size)}
                          </div>
                        </div>
                      </div>
                      <div className="tw-justify-end tw-items-center tw-gap-1 tw-flex">
                        <div
                          onClick={() => window.open(file.preview, "_blank")}
                          onKeyDown={() => window.open(file.preview, "_blank")}
                          role="button"
                          tabIndex={0}
                          className="tw-text-center tw-text-blue-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                        >
                          {watch("media.category") === "image" ? (
                            t("MacBotMediaForm/key7")
                          ) : (
                            <BiPlay size={22} className="tw-text-gray-400" />
                          )}
                        </div>
                        <button
                          className="tw-bg-transparent tw-border-none"
                          type="button"
                          onClick={handleDeleteSelectedFile}
                        >
                          <BiTrash size={22} className="tw-text-gray-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </aside>
            </section>
          )}
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {isDelete && (
              <DeleteButton
                deleteHandler={deleteHandler}
                onKeyDown={deleteHandler}
                tabIndex={0}
              />
            )}

            {itemData?.id && (
              <NodeParentChanger tree={tree} currentId={itemData.id} />
            )}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex"
              disabled={loading || cdnUploadResults.isLoading}
            >
              {loading || cdnUploadResults.isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("MacBotResponseForm/btn-02")
              )}
            </Button>
          </div>
        </div>
        <NoteForm
          noteFormMethods={{ register, watch, errors, fields, setValue }}
        />
      </ScenarioSide>
    </React.Fragment>
  );
};
